/**
 * Best For Page
 *
 * Displays best gear recommendations for a specific activity.
 * Route: /best-for/:activity
 *
 * Features:
 * - Activity title as h1
 * - Intro/outro content blocks
 * - Product grid per category (budget/mid/premium)
 * - SEO meta tags and JSON-LD structured data
 * - Error states for unknown activities
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchBestFor, getProductImageUrl, type BestForProduct, type BestForCategory } from '../lib/api';
import { ProductDetailModal } from '../components/ProductDetailModal';
import { useComparison } from '../hooks/useComparison';

function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)}kg`;
  }
  return `${grams}g`;
}

function formatActivityName(activity: string): string {
  return activity.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
}

function getTierBadge(tier: BestForProduct['tier']) {
  switch (tier) {
    case 'budget':
      return (
        <span className="px-2 py-0.5 bg-green-500/10 text-green-400 text-xs rounded">
          Budget
        </span>
      );
    case 'mid':
      return (
        <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-xs rounded">
          Mid
        </span>
      );
    case 'premium':
      return (
        <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 text-xs rounded">
          Premium
        </span>
      );
  }
}

function ProductImagePlaceholder() {
  return (
    <div className="w-20 h-20 flex items-center justify-center bg-neutral-800 rounded-lg shrink-0">
      <svg
        className="w-8 h-8 text-neutral-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        />
      </svg>
    </div>
  );
}

interface ProductImageProps {
  src: string;
  alt: string;
  onError: () => void;
}

function ProductImage({ src, alt, onError }: ProductImageProps) {
  return (
    <img
      src={src}
      alt={alt}
      onError={onError}
      className="w-20 h-20 object-cover rounded-lg shrink-0 bg-neutral-800"
    />
  );
}

interface ProductCardComponentProps {
  product: BestForProduct;
  onViewDetails?: (productId: string) => void;
}

function ProductCard({ product, onViewDetails }: ProductCardComponentProps) {
  const [imageError, setImageError] = useState(false);

  const imageUrl = getProductImageUrl(product.cf_image_id, product.image_url);
  const hasValidImage = imageUrl && !imageError;
  const imageAlt = product.image_alt || product.name;

  return (
    <div className="bg-neutral-900 border border-neutral-800 hover:border-neutral-700 rounded-lg p-5 transition-colors">
      <div className="flex gap-4 mb-3">
        {/* Product Image */}
        {hasValidImage ? (
          <ProductImage
            src={imageUrl}
            alt={imageAlt}
            onError={() => setImageError(true)}
          />
        ) : (
          <ProductImagePlaceholder />
        )}

        <div className="flex justify-between items-start gap-3 flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {getTierBadge(product.tier)}
            </div>
            <h3 className="font-semibold text-white text-lg truncate">
              {product.name}
            </h3>
          </div>
          <div className="text-right shrink-0">
            <p className="font-mono font-bold text-xl text-white">
              £{product.price_gbp}
            </p>
            <p className="font-mono text-sm text-neutral-500">
              {formatWeight(product.weight_g)}
            </p>
          </div>
        </div>
      </div>
      <p className="text-neutral-400 text-sm leading-relaxed mb-3">
        {product.why}
      </p>

      {/* Actions */}
      {onViewDetails && (
        <button
          onClick={() => onViewDetails(product.id)}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-400 hover:text-white hover:bg-neutral-800 rounded transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>View Details</span>
        </button>
      )}
    </div>
  );
}

interface CategorySectionProps {
  category: BestForCategory;
  onViewDetails?: (productId: string) => void;
}

function CategorySection({ category, onViewDetails }: CategorySectionProps) {
  return (
    <section className="mb-10">
      <h2 className="font-mono text-xl font-bold text-white mb-4">
        {category.name}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {category.products.map((product) => (
          <ProductCard key={product.id} product={product} onViewDetails={onViewDetails} />
        ))}
      </div>
    </section>
  );
}

function BestForStructuredData({
  title,
  categories,
}: {
  title: string;
  categories: BestForCategory[];
}) {
  const allProducts = categories.flatMap((cat) => cat.products);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: title,
    numberOfItems: allProducts.length,
    itemListElement: allProducts.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: product.name,
        offers: {
          '@type': 'Offer',
          price: product.price_gbp,
          priceCurrency: 'GBP',
        },
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

function setMetaTags(title: string, activity: string) {
  // Set meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute(
    'content',
    `Best gear recommendations for ${activity}. Expert-curated equipment across budget, mid-range, and premium tiers.`
  );

  // Set OG title
  let ogTitle = document.querySelector('meta[property="og:title"]');
  if (!ogTitle) {
    ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    document.head.appendChild(ogTitle);
  }
  ogTitle.setAttribute('content', title);
}

function cleanupMetaTags() {
  const metaDescription = document.querySelector('meta[name="description"]');
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (metaDescription) metaDescription.remove();
  if (ogTitle) ogTitle.remove();
}

export function BestFor() {
  const { activity } = useParams<{ activity: string }>();
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const { addProduct, isInComparison } = useComparison();

  const { data, isLoading, error } = useQuery({
    queryKey: ['best-for', activity],
    queryFn: () => fetchBestFor(activity!),
    enabled: !!activity,
  });

  const handleViewDetails = (productId: string) => {
    setSelectedProductId(productId);
  };

  const handleCloseModal = () => {
    setSelectedProductId(null);
  };

  const handleAddToCompare = (productId: string) => {
    // Find the product in the data to get full info
    if (!data) return;
    for (const category of data.categories) {
      const product = category.products.find(p => p.id === productId);
      if (product) {
        addProduct({
          id: product.id,
          name: product.name,
          category_id: category.id,
          price_gbp: product.price_gbp,
          weight_g: product.weight_g,
          tier: product.tier,
        });
        break;
      }
    }
  };

  // Update document title and meta tags
  useEffect(() => {
    if (data?.title) {
      document.title = `${data.title} | NFA`;
      setMetaTags(data.title, data.activity);
    }
    return () => {
      document.title = 'NFA - Gear Recommendations';
      cleanupMetaTags();
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
        </div>
      </div>
    );
  }

  if (error) {
    const is404 = (error as { status?: number }).status === 404;

    return (
      <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6">
        <nav className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Home
          </Link>
        </nav>

        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-8 text-center">
          {is404 ? (
            <>
              <h2 className="font-mono text-xl font-bold text-red-400 mb-2">Activity Not Found</h2>
              <p className="text-neutral-400 mb-4">
                The activity you're looking for doesn't exist or has been removed.
              </p>
              <Link
                to="/gear"
                className="inline-block px-4 py-2 bg-orange-500 text-black font-medium rounded-lg hover:bg-orange-400 transition-colors"
              >
                Browse activities
              </Link>
            </>
          ) : (
            <>
              <h2 className="font-mono text-xl font-bold text-red-400 mb-2">Something went wrong</h2>
              <p className="text-neutral-400 mb-4">
                We couldn't load the gear recommendations. Please try again later.
              </p>
              <Link
                to="/"
                className="inline-block px-4 py-2 bg-orange-500 text-black font-medium rounded-lg hover:bg-orange-400 transition-colors"
              >
                Go home
              </Link>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6">
      <BestForStructuredData title={data.title} categories={data.categories} />

      {/* Breadcrumb */}
      <nav className="mb-6">
        <Link
          to="/best-for"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Best Gear Guides
        </Link>
      </nav>

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-mono text-3xl sm:text-4xl font-bold text-white mb-4">
          {data.title}
        </h1>
        {/* Intro content */}
        <div
          className="prose prose-invert prose-neutral max-w-none text-neutral-400"
          dangerouslySetInnerHTML={{ __html: data.intro_html }}
        />
      </div>

      {/* Category sections */}
      {data.categories.map((category) => (
        <CategorySection key={category.id} category={category} onViewDetails={handleViewDetails} />
      ))}

      {/* Product Detail Modal */}
      <ProductDetailModal
        productId={selectedProductId}
        isOpen={selectedProductId !== null}
        onClose={handleCloseModal}
        onAddToCompare={handleAddToCompare}
        isInCompare={selectedProductId ? isInComparison(selectedProductId) : false}
      />

      {/* Outro content */}
      <div
        className="prose prose-invert prose-neutral max-w-none text-neutral-400 mb-12"
        dangerouslySetInnerHTML={{ __html: data.outro_html }}
      />

      {/* CTA */}
      <div className="bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-6 sm:p-8">
        <h2 className="font-mono text-xl sm:text-2xl font-bold text-white mb-3">
          Need a complete kit?
        </h2>
        <p className="text-neutral-400 mb-6">
          Tell us about your trip and we'll put together the perfect gear list.
        </p>
        <Link
          to="/"
          state={{ prefillQuery: `${formatActivityName(data.activity)} trip in the UK` }}
          className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold rounded-lg hover:bg-orange-400 transition-colors"
        >
          <span>Build your kit</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
